import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/core';
import {
  roaryImportApi,
  type ImportEvent,
  type ImportSessionDetails,
  type ProposedPlan,
} from '../api/modules/roaryImport';

const IN_FLIGHT_STATUSES = new Set([
  'pending',
  'scanning',
  'analyzing',
  'awaiting_confirmation',
  'building',
]);

function sessionToConflict(session: ImportSessionDetails): ConflictInfo {
  return {
    sessionId: session.id,
    sourceOrigin: session.sourceOrigin,
    sourceUrl: session.sourceUrl,
    status: session.status,
    createdAt: session.createdAt ? String(session.createdAt) : undefined,
  };
}

async function findInFlightConflict(): Promise<ConflictInfo | null> {
  try {
    const { sessions } = await roaryImportApi.listSessions();
    const inFlight = sessions.find(s => IN_FLIGHT_STATUSES.has(s.status));
    return inFlight ? sessionToConflict(inFlight) : null;
  } catch {
    return null;
  }
}

function conflictFromCreateError(err: unknown): {
  message: string;
  conflict: ConflictInfo | null;
  needsLookup: boolean;
} {
  const apiErr = err instanceof ApiError ? err : null;
  const axiosBody = (err as { response?: { data?: Record<string, unknown>; status?: number } })
    ?.response;
  const status = apiErr?.status ?? axiosBody?.status;
  const body = (apiErr?.data ?? axiosBody?.data ?? {}) as Record<string, unknown>;
  const message =
    (typeof body.message === 'string' && body.message) ||
    (typeof body.error === 'string' && body.error) ||
    (err instanceof Error ? err.message : 'Failed to start import');

  if (status === 409 && typeof body.sessionId === 'string') {
    return {
      message,
      needsLookup: false,
      conflict: {
        sessionId: body.sessionId,
        sourceOrigin: typeof body.sourceOrigin === 'string' ? body.sourceOrigin : undefined,
        sourceUrl: typeof body.sourceUrl === 'string' ? body.sourceUrl : undefined,
        status: typeof body.status === 'string' ? body.status : undefined,
        createdAt:
          typeof body.createdAt === 'string'
            ? body.createdAt
            : body.createdAt instanceof Date
              ? body.createdAt.toISOString()
              : undefined,
      },
    };
  }
  return { message, conflict: null, needsLookup: status === 409 };
}

export type ImportPhase =
  | 'picking_layout'
  | 'idle'
  | 'creating'
  | 'scanning'
  | 'analyzing'
  | 'awaiting_page_selection'
  | 'awaiting_confirmation'
  | 'building'
  | 'completed'
  | 'failed';

export interface DiscoveredPage {
  url: string;
  path: string;
  depth: number;
  title?: string | null;
  status: 'pending' | 'parsed' | 'failed';
  thumbnailBase64?: string;
  error?: string;
}

export interface ConflictInfo {
  sessionId: string;
  sourceOrigin?: string;
  sourceUrl?: string;
  status?: string;
  createdAt?: string;
}

export interface UseRoaryImportState {
  phase: ImportPhase;
  sessionId: string | null;
  sourceUrl: string | null;
  marshmallowsRemaining: number | null;
  pages: Map<string, DiscoveredPage>;
  assetsDiscovered: number;
  brandColors: Record<string, string> | null;
  brandFonts: string[];
  logoUrl: string | null;
  plan: ProposedPlan | null;
  builtSiteId: string | null;
  builtPageIds: string[];
  /** Build progress: per-page status keyed by display title. */
  buildProgress: Array<{
    title: string;
    status: 'pending' | 'in_progress' | 'done';
    pageId?: string;
  }>;
  error: string | null;
  warnings: string[];
  /** Set when createSession returns 409; the previous in-flight session blocking us. */
  conflict: ConflictInfo | null;
  layoutTemplateId: string | null;
  layoutTemplateName: string | null;
}

const initialState: UseRoaryImportState = {
  phase: 'picking_layout',
  sessionId: null,
  sourceUrl: null,
  marshmallowsRemaining: null,
  pages: new Map(),
  assetsDiscovered: 0,
  brandColors: null,
  brandFonts: [],
  logoUrl: null,
  plan: null,
  builtSiteId: null,
  builtPageIds: [],
  buildProgress: [],
  error: null,
  warnings: [],
  conflict: null,
  layoutTemplateId: null,
  layoutTemplateName: null,
};

export function useRoaryImport() {
  const [state, setState] = useState<UseRoaryImportState>(initialState);
  const abortRef = useRef<{ abort: () => void } | null>(null);

  // Cleanup on unmount
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  // Surface a stuck in-flight session as soon as Import opens — don't wait
  // for the user to submit and hit a 409.
  useEffect(() => {
    let cancelled = false;
    findInFlightConflict().then(conflict => {
      if (cancelled || !conflict) return;
      setState(s =>
        s.phase === 'picking_layout' || s.phase === 'idle' || s.phase === 'failed'
          ? { ...s, conflict }
          : s
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState(initialState);
  }, []);

  const selectLayout = useCallback((template: { id: string; name: string }) => {
    setState(s => ({
      ...s,
      phase: 'idle',
      layoutTemplateId: template.id,
      layoutTemplateName: template.name,
    }));
  }, []);

  const backToLayout = useCallback(() => {
    setState(s => ({
      ...s,
      phase: 'picking_layout',
      error: null,
    }));
  }, []);

  const startImport = useCallback(async (url: string) => {
    abortRef.current?.abort();
    abortRef.current = null;
    const templateId = await new Promise<string | null>(resolve =>
      setState(s => {
        resolve(s.layoutTemplateId);
        return {
          ...s,
          phase: 'creating' as const,
          sourceUrl: url,
          error: null,
          conflict: null,
        };
      })
    );
    if (!templateId) {
      setState(s => ({
        ...s,
        phase: 'picking_layout',
        error: 'Select a layout before starting the scan.',
      }));
      return null;
    }

    let session;
    try {
      session = await roaryImportApi.createSession(url, { templateId });
    } catch (err: unknown) {
      const parsed = conflictFromCreateError(err);
      let conflict = parsed.conflict;
      if (!conflict && (parsed.needsLookup || /import already/i.test(parsed.message))) {
        conflict = await findInFlightConflict();
      }
      setState(s => ({
        ...s,
        phase: 'failed',
        error: parsed.message,
        conflict,
      }));
      return null;
    }

    setState(s => ({
      ...s,
      phase: 'scanning',
      sessionId: session.sessionId,
      marshmallowsRemaining: session.marshmallowsRemaining,
    }));

    abortRef.current = roaryImportApi.startScan(session.sessionId, applyEvent);
    return session.sessionId;
  }, []);

  const cancelInFlight = useCallback(async () => {
    const conflictId = await new Promise<string | null>(resolve =>
      setState(s => {
        resolve(s.conflict?.sessionId ?? null);
        return { ...s, error: null };
      })
    );
    if (conflictId) {
      try {
        await roaryImportApi.cancelSession(conflictId);
      } catch {
        // Already terminal is fine.
      }
    }
    setState(s => ({
      ...s,
      conflict: null,
      error: null,
      phase: s.layoutTemplateId ? 'idle' : 'picking_layout',
    }));
  }, []);

  const cancelConflictAndRetry = useCallback(
    async (url: string) => {
      await cancelInFlight();
      if (url.trim()) return startImport(url);
    },
    [cancelInFlight, startImport]
  );

  const confirmPages = useCallback(async (paths: string[]) => {
    const sessionId = await new Promise<string | null>(resolve =>
      setState(s => {
        resolve(s.sessionId);
        return { ...s, phase: 'scanning' as const };
      })
    );
    if (!sessionId) return;
    abortRef.current = roaryImportApi.startScan(sessionId, applyEvent, { paths });
  }, []);

  const confirmPlan = useCallback(async (plan: ProposedPlan) => {
    setState(s => {
      if (!s.sessionId) return s;
      return {
        ...s,
        phase: 'building',
        plan,
        buildProgress: plan.pages.map(p => ({ title: p.title, status: 'pending' as const })),
        builtSiteId: null,
        builtPageIds: [],
      };
    });
    const sessionId = await new Promise<string | null>(resolve =>
      setState(s => {
        resolve(s.sessionId);
        return s;
      })
    );
    if (!sessionId) return;
    abortRef.current = roaryImportApi.startBuild(sessionId, plan, applyEvent);
  }, []);

  const cancel = useCallback(async () => {
    const sessionId = await new Promise<string | null>(resolve =>
      setState(s => {
        resolve(s.sessionId);
        return s;
      })
    );
    abortRef.current?.abort();
    abortRef.current = null;
    if (sessionId) {
      try {
        await roaryImportApi.cancelSession(sessionId);
      } catch {
        // best-effort
      }
    }
    setState(initialState);
  }, []);

  const applyEvent = useCallback((event: ImportEvent) => {
    setState(s => reduce(s, event));
  }, []);

  return {
    state,
    selectLayout,
    backToLayout,
    startImport,
    confirmPages,
    confirmPlan,
    cancel,
    cancelInFlight,
    cancelConflictAndRetry,
    reset,
  };
}

function reduce(state: UseRoaryImportState, event: ImportEvent): UseRoaryImportState {
  const data = event.data as Record<string, any>;
  switch (event.type) {
    case 'import.scan_started':
      return { ...state, phase: 'scanning' };

    case 'import.page_discovered': {
      const next = new Map(state.pages);
      const path = String(data.path ?? data.url);
      const existing = next.get(path);
      if (existing?.status === 'parsed') {
        return { ...state, pages: next };
      }
      next.set(path, {
        url: String(data.url),
        path,
        depth: Number(data.depth ?? existing?.depth ?? 0),
        title: typeof data.label === 'string' ? data.label : existing?.title,
        status: 'pending',
      });
      return { ...state, pages: next };
    }

    case 'import.page_parsed': {
      const next = new Map(state.pages);
      const path = String(data.path);
      const existing = next.get(path);
      next.set(path, {
        url: String(data.url),
        path,
        depth: existing?.depth ?? 0,
        title: data.title ?? null,
        status: 'parsed',
        thumbnailBase64: data.screenshotBase64,
      });
      return {
        ...state,
        pages: next,
        assetsDiscovered: state.assetsDiscovered + Number(data.assetsFound ?? 0),
      };
    }

    case 'import.page_failed': {
      const next = new Map(state.pages);
      const path = String(data.path);
      const existing = next.get(path);
      next.set(path, {
        url: String(data.url),
        path,
        depth: existing?.depth ?? 0,
        status: 'failed',
        error: String(data.error ?? ''),
      });
      return { ...state, pages: next };
    }

    case 'import.brand_extracted':
      return {
        ...state,
        brandColors: (data.colors as Record<string, string>) ?? null,
        brandFonts: Array.isArray(data.fonts) ? (data.fonts as string[]) : [],
        logoUrl: typeof data.logoUrl === 'string' ? data.logoUrl : state.logoUrl,
      };

    case 'import.analysis_started':
      return { ...state, phase: 'analyzing' };

    case 'import.plan_ready':
      return {
        ...state,
        phase: 'awaiting_confirmation',
        plan: data.plan as ProposedPlan,
        warnings:
          (data.plan as ProposedPlan)?.warnings &&
          Array.isArray((data.plan as ProposedPlan).warnings)
            ? ((data.plan as ProposedPlan).warnings as string[])
            : [],
      };

    case 'import.build_page_started':
      return {
        ...state,
        buildProgress: state.buildProgress.map((p, i) =>
          i === Number(data.index) ? { ...p, status: 'in_progress' } : p
        ),
      };

    case 'import.build_page_done':
      return {
        ...state,
        buildProgress: state.buildProgress.map(p =>
          p.title === data.title ? { ...p, status: 'done', pageId: String(data.pageId) } : p
        ),
        builtPageIds: [...state.builtPageIds, String(data.pageId)],
      };

    case 'import.site_created':
      return { ...state, builtSiteId: String(data.siteId) };

    case 'import.completed':
      return {
        ...state,
        phase: 'completed',
        builtSiteId: String(data.siteId),
        builtPageIds: Array.isArray(data.pageIds) ? (data.pageIds as string[]) : state.builtPageIds,
      };

    case 'import.failed':
      return {
        ...state,
        phase: 'failed',
        error: typeof data.message === 'string' ? data.message : 'Import failed',
      };

    case 'done':
      if (data.awaitingPageSelection && state.phase !== 'failed') {
        return { ...state, phase: 'awaiting_page_selection' };
      }
      return state;

    default:
      return state;
  }
}
